import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import PlannedModule from "@/pages/PlannedModule";
import Dashboard from "@/pages/Dashboard";
import ActivityStart from "@/pages/ActivityStart";
import POS from "@/pages/POS";
import BOMHPPPage from "@/pages/BOMHPP";
import OutletsPage from "@/pages/Outlets";
import TransactionsPage from "@/pages/Transactions";
import InventoryPage from "@/pages/Inventory";
import StockInPage from "@/pages/StockIn";
import StockOutPage from "@/pages/StockOut";
import StockRequestPage from "@/pages/StockRequest";
import AuditPage from "@/pages/Audit";
import ExpensesPage from "@/pages/Expenses";
import TransfersPage from "@/pages/Transfers";
import ReportsPage from "@/pages/Reports";
import ReconciliationPage from "@/pages/Reconciliation";
import MasterDataPage from "@/pages/MasterData";
import MasterDataCatalog from "@/pages/MasterDataCatalog";
import CustomersPage from "@/pages/Customers";
import CompanySetupPage from "@/pages/CompanySetup";
import CompanyProfilePage from "@/pages/CompanyProfile";
import PurchasingPage from "@/pages/Purchasing";
import SheetsPage from "@/pages/Sheets";
import PromosPage from "@/pages/Promos";
import HRPage from "@/pages/HR";
import PayrollPage from "@/pages/Payroll";
import FinancePage from "@/pages/Finance";
import CashBankPage from "@/pages/CashBank";
import FixedAssetsPage from "@/pages/FixedAssets";
import CommandCenterPage from "@/pages/CommandCenter";
import ConfigurationPage from "@/pages/Configuration";
import ShiftsPage from "@/pages/Shifts";
import AccessControlPage from "@/pages/AccessControl";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/pos" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/activity" element={<ActivityStart />} />
        <Route path="/planned/:moduleSlug" element={<PlannedModule />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pos" element={<POS />} />
        <Route path="/shifts" element={<Protected roles={["owner", "manager", "supervisor", "kasir", "cashier_outlet"]}><ShiftsPage /></Protected>} />
        <Route path="/products" element={<Navigate to="/bom-hpp" replace />} />
        <Route path="/bom-hpp" element={<Protected roles={["owner", "manager"]}><BOMHPPPage /></Protected>} />
        <Route path="/outlets" element={<Protected roles={["owner", "manager"]}><OutletsPage /></Protected>} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/stock-in" element={<StockInPage />} />
        <Route path="/stock-out" element={<StockOutPage />} />
        <Route path="/stock-request" element={<StockRequestPage />} />
        <Route path="/transfers" element={<Protected roles={["owner", "manager", "supervisor", "gudang"]}><TransfersPage /></Protected>} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/reports" element={<Protected roles={["owner", "manager", "keuangan"]}><ReportsPage /></Protected>} />
        <Route path="/reconciliation" element={<Protected roles={["owner", "manager", "keuangan"]}><ReconciliationPage /></Protected>} />
        <Route path="/audit" element={<Protected roles={["owner", "manager"]}><AuditPage /></Protected>} />
        <Route path="/company-setup" element={<Protected roles={["owner"]}><CompanySetupPage /></Protected>} />
        <Route path="/company-profile" element={<Protected roles={["owner"]}><CompanyProfilePage /></Protected>} />
    <Route path="/master-data" element={<Protected roles={["owner"]}><MasterDataPage /></Protected>} />
        <Route path="/master-catalog/:slug" element={<Protected roles={["owner"]}><MasterDataCatalog /></Protected>} />
        <Route path="/customers" element={<Protected roles={["owner", "manager"]}><CustomersPage /></Protected>} />
        <Route path="/access-control" element={<Protected roles={["owner"]}><AccessControlPage /></Protected>} />
        <Route path="/configuration" element={<Protected roles={["owner", "manager"]}><ConfigurationPage /></Protected>} />
        <Route path="/purchasing" element={<Protected roles={["owner", "manager", "gudang"]}><PurchasingPage /></Protected>} />
        <Route path="/sheets" element={<Protected roles={["owner", "manager", "keuangan"]}><SheetsPage /></Protected>} />
        <Route path="/promos" element={<Protected roles={["owner", "manager"]}><PromosPage /></Protected>} />
        <Route path="/hr" element={<Protected roles={["owner", "manager", "supervisor"]}><HRPage /></Protected>} />
        <Route path="/payroll" element={<Protected roles={["owner", "manager", "keuangan"]}><PayrollPage /></Protected>} />
        <Route path="/finance" element={<Protected roles={["owner", "manager", "keuangan", "supervisor"]}><FinancePage /></Protected>} />
        <Route path="/cash-bank" element={<Protected roles={["owner", "manager", "keuangan"]}><CashBankPage /></Protected>} />
        <Route path="/fixed-assets" element={<Protected roles={["owner", "manager", "keuangan"]}><FixedAssetsPage /></Protected>} />
        <Route path="/command-center" element={<Protected roles={["owner", "manager", "keuangan"]}><CommandCenterPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
